import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { subscribeToast } from "../utils/toastBus";

const ToastContext = createContext(null);

let nextId = 1;
const AUTO_DISMISS_MS = 4500;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    setToasts(list => list.filter(t => t.id !== id));
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
  }, []);

  const push = useCallback(({ type = "info", text }) => {
    if (!text) return;
    const id = nextId++;
    setToasts(list => [...list, { id, type, text }]);
    timersRef.current[id] = setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
  }, [dismiss]);

  // Toasts pushed from outside React (axios interceptors) arrive here.
  useEffect(() => subscribeToast(push), [push]);

  const toastSuccess = useCallback((text) => push({ type: "success", text }), [push]);
  const toastError = useCallback((text) => push({ type: "error", text }), [push]);
  const toastInfo = useCallback((text) => push({ type: "info", text }), [push]);

  return (
    <ToastContext.Provider value={{ toasts, dismiss, toastSuccess, toastError, toastInfo }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
